import { IShift } from "../../../types/shifts/Shift";
import { IShiftEnrolment } from "../../../types/shifts/ShiftEnrolment";
import * as types from "../../actions/actionTypes";
import { IProcessedShiftSessionTransport } from "../../api/shiftsApi";
import { IShiftState, InitialState } from "../state";

const getSetWithNewIds = (ids: string[], newIds: string[]) =>
  Array.from(new Set(ids.concat(newIds)));

const getUpdatedStateWithNewShifts = (
  state: IShiftState,
  shifts: IShift[],
) => ({
  ...state,
  map: {
    ...state.map,
    ...shifts.reduce((mp: any, shft) => {
      const existingShift = shft._id in state.map && state.map[shft._id];
      mp[shft._id] = { ...(existingShift || {}), ...shft };
      return mp;
    }, {}),
  },
});

const reducers = {
  [types.SHIFTS_GET_SUPERVISABLE_FULFILLED]: (state: IShiftState, shifts: IShift[]) => ({
    ...getUpdatedStateWithNewShifts(state, shifts),
    supervisable: shifts.map((s) => s._id),
    loading: {
      ...state.loading,
      supervisable: { loading: false, loaded: true },
    },
  }),
  [types.SHIFTS_GET_SHIFT_STARTED]: (state: IShiftState, shiftKey: string) => ({
    ...state,
    loading: { ...state.loading, [shiftKey]: { loading: true } },
  }),
  [types.SHIFTS_GET_SHIFT_REJECTED]: (state: IShiftState, { shiftKey, message }: { shiftKey: string, message: string }) => ({
    ...state,
    loading: {
      ...state.loading,
      [shiftKey]: { loading: false, loaded: true, error: message },
    },
  }),

  [types.SHIFTS_GET_MANAGEABLE_FULFILLED]: (state: IShiftState, { shifts }: { shifts: IShift[] }) => ({
    ...getUpdatedStateWithNewShifts(state, shifts),
    manageable: getSetWithNewIds(
      state.manageable,
      shifts.map((s) => s._id),
    ),
  }),
  [types.SHIFTS_CREATE_SHIFT_FULFILLED]: (state: IShiftState, shift: IShift) => ({
    ...getUpdatedStateWithNewShifts(state, [shift]),
    manageable: getSetWithNewIds(state.manageable, [shift._id]),
    supervisable: getSetWithNewIds(state.supervisable, [shift._id]),
  }),
  [types.SHIFTS_UPDATE_SHIFT_FULFILLED]: (state: IShiftState, shifts: IShift[]) => ({
    ...getUpdatedStateWithNewShifts(state, shifts),
  }),
  [types.SHIFTS_GET_SHIFT_FULFILLED]: (state: IShiftState, data: IProcessedShiftSessionTransport) => ({
    ...state,
    filled: { ...state.filled, [data.processed.session._id]: data },
    loading: {
      ...state.loading,
      [data.processed.session._id]: { loading: false, loaded: true },
    },
  }),

  [types.SHIFTS_ADD_ENROLMENT_FULFILLED]: (
    state: IShiftState,
    { enrolment }: { enrolment: IShiftEnrolment },
  ) => ({
    ...state,
    map: {
      ...state.map,
      [enrolment.shift]: {
        ...state.map[enrolment.shift],
        enrolments: [...(state.map[enrolment.shift].enrolments || []), enrolment],
      },
    },
  }),
  [types.APPOINTMENTS_GET_ACTIONABLE_FULFILLED]: (
    state: IShiftState,
    { shifts }: { shifts: IShift[] },
  ) => getUpdatedStateWithNewShifts(state, shifts),
  [types.SHIFTS_UPDATE_ENROLMENT_FULFILLED]: (
    state: IShiftState,
    { enrolment }: { enrolment: IShiftEnrolment },
  ) => {
    const shiftToUpdate: IShift = state.map[enrolment.shift];
    const indexToUpdate = shiftToUpdate.enrolments.findIndex(
      (e) => e.person === enrolment.person,
    );

    if (indexToUpdate < 0) return state; // something happened :/

    const newEnrolments = [...shiftToUpdate.enrolments];
    newEnrolments[indexToUpdate] = enrolment;

    return {
      ...state,
      map: {
        ...state.map,
        [enrolment.shift]: {
          ...shiftToUpdate,
          enrolments: newEnrolments,
        },
      },
    };
  },
};

export default function shiftReducer(
  state: IShiftState = InitialState.shifts,
  action: any,
) {
  return action.type in reducers
    ? (reducers as any)[action.type](state, action.payload)
    : state;
}
